import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  ArrowLeft,
  Package,
  TrendingUp,
  TruckIcon,
  Plus,
  Search,
  Calendar,
  Shirt,
  Filter,
  History,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import { fetchInventarioVertical } from '@/services/inventarioLenceriaVertical.service';
import { fetchMovimientosVertical } from '@/services/movimientosLenceriaVertical.service';
import { fetchEntregasVertical } from '@/services/entregasLenceriaVertical.service';
import CrearEntregaDirectaModal from '@/components/lenceria/CrearEntregaDirectaModal';
import DetalleEntregaVerticalModal from '@/components/lenceria/DetalleEntregaVerticalModal';
import DetalleAlistamientoModal from '@/components/lenceria/DetalleAlistamientoModal';
import DetalleRecoleccionModal from '@/components/lenceria/DetalleRecoleccionModal';
import EditarAlistamientoModal from '@/components/lenceria/EditarAlistamientoModal';

const ITEMS_PER_PAGE = 10;

const EdificioInventarioDetail = () => {
  const { edificioId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [edificio, setEdificio] = useState(location.state?.edificio || null);
  const [activeTab, setActiveTab] = useState('inventario');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detectar si viene desde HospedajesTable (desde portafolios)
  const fromHospedajes = location.state?.fromHospedajes || false;
  
  // Estados para filtros
  const [filters, setFilters] = useState({});
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  
  // Total de prendas en inventario
  const [totalPrendas, setTotalPrendas] = useState(0);

  // Estados para Inventario
  const [inventario, setInventario] = useState([]);
  const [isLoadingInventario, setIsLoadingInventario] = useState(false);
  const [currentPageInventario, setCurrentPageInventario] = useState(1);
  const [totalInventario, setTotalInventario] = useState(0);

  // Estados para Movimientos
  const [movimientos, setMovimientos] = useState([]);
  const [isLoadingMovimientos, setIsLoadingMovimientos] = useState(false);
  const [currentPageMovimientos, setCurrentPageMovimientos] = useState(1);
  const [totalMovimientos, setTotalMovimientos] = useState(0);

  // Estados para Entregas
  const [entregas, setEntregas] = useState([]);
  const [isLoadingEntregas, setIsLoadingEntregas] = useState(false);
  const [currentPageEntregas, setCurrentPageEntregas] = useState(1);
  const [totalEntregas, setTotalEntregas] = useState(0);

  // Estados para Recolecciones
  const [recolecciones, setRecolecciones] = useState([]);
  const [isLoadingRecolecciones, setIsLoadingRecolecciones] = useState(false);
  const [currentPageRecolecciones, setCurrentPageRecolecciones] = useState(1);
  const [totalRecolecciones, setTotalRecolecciones] = useState(0);

  // Estado para modal de nueva entrega
  const [showCrearEntregaModal, setShowCrearEntregaModal] = useState(false);
  
  // Estados para modales de detalle
  const [selectedEntrega, setSelectedEntrega] = useState(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [selectedAlistamiento, setSelectedAlistamiento] = useState(null);
  const [showAlistamientoModal, setShowAlistamientoModal] = useState(false);
  const [selectedRecoleccion, setSelectedRecoleccion] = useState(null);
  const [showRecoleccionModal, setShowRecoleccionModal] = useState(false);
  const [selectedAlistamientoEdit, setSelectedAlistamientoEdit] = useState(null);
  const [showEditarAlistamientoModal, setShowEditarAlistamientoModal] = useState(false);

  // Estados para Historial Detallado
  const [historialDetallado, setHistorialDetallado] = useState([]);
  const [isLoadingHistorial, setIsLoadingHistorial] = useState(false);
  const [currentPageHistorial, setCurrentPageHistorial] = useState(1);
  const [totalHistorial, setTotalHistorial] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});

  // Cargar edificio si no viene en el state
  useEffect(() => {
    if (!edificio && edificioId) {
      loadEdificio();
    }
  }, [edificioId]);

  const loadEdificio = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/edificios/${edificioId}`);
      if (response.ok) {
        const data = await response.json();
        setEdificio(data);
      }
    } catch (error) {
      console.error('Error al cargar edificio:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'inventario') {
      loadInventario();
    }
  }, [activeTab, currentPageInventario, edificioId, searchTerm]);

  useEffect(() => {
    if (activeTab === 'movimientos') {
      loadMovimientos();
    }
  }, [activeTab, currentPageMovimientos, edificioId, searchTerm, filters]);

  useEffect(() => {
    if (activeTab === 'entregas') {
      loadEntregas();
    }
  }, [activeTab, currentPageEntregas, edificioId, searchTerm, filters]);

  useEffect(() => {
    if (activeTab === 'recolecciones') {
      loadRecolecciones();
    }
  }, [activeTab, currentPageRecolecciones, edificioId, searchTerm, filters]);

  useEffect(() => {
    if (activeTab === 'historial') {
      loadHistorialDetallado();
    }
  }, [activeTab, currentPageHistorial, edificioId, searchTerm, filters]);

  const loadInventario = async () => {
    setIsLoadingInventario(true);
    try {
      const response = await fetchInventarioVertical({
        edificioId,
        page: currentPageInventario,
        limit: 100,
        search: searchTerm
      });
        
      setInventario(response.data || []);
      setTotalInventario(response.total || 0);
      
      // Calcular total de prendas
      const total = (response.data || []).reduce((sum, item) => sum + (item.cantidad_disponible || 0), 0);
      setTotalPrendas(total);
    } catch (error) {
      console.error('Error al cargar inventario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingInventario(false);
    }
  };

  const loadMovimientos = async () => {
    setIsLoadingMovimientos(true);
    try {
      const params = {
        edificioId,
        page: currentPageMovimientos,
        limit: ITEMS_PER_PAGE,
        search: searchTerm
      };
      
      // Agregar filtros de fecha si existen
      if (filters.fechaInicio) {
        params.fechaInicio = filters.fechaInicio;
      }
      if (filters.fechaFin) {
        params.fechaFin = filters.fechaFin;
      }
      
      const response = await fetchMovimientosVertical(params);
      setMovimientos(response.data || []);
      setTotalMovimientos(response.total || 0);
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los movimientos',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingMovimientos(false);
    }
  };

  const loadEntregas = async () => {
    setIsLoadingEntregas(true);
    try {
      const params = {
        edificioId,
        page: currentPageEntregas,
        limit: ITEMS_PER_PAGE,
        search: searchTerm,
        estado: { $nin: ['recoleccion', 'pendiente_confirmacion'] } // Excluir recolecciones y pendientes de confirmación
      };
      
      // Agregar filtros de fecha si existen
      if (filters.fechaInicio) {
        params.fechaInicio = filters.fechaInicio;
      }
      if (filters.fechaFin) {
        params.fechaFin = filters.fechaFin;
      }
      
      const response = await fetchEntregasVertical(params);
      setEntregas(response.data || []);
      setTotalEntregas(response.total || 0);
    } catch (error) {
      console.error('Error al cargar entregas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingEntregas(false);
    }
  };

  const loadRecolecciones = async () => {
    setIsLoadingRecolecciones(true);
    try {
      const params = {
        edificioId,
        page: currentPageRecolecciones,
        limit: ITEMS_PER_PAGE,
        search: searchTerm,
        estado: 'recoleccion'
      };
      
      // Agregar filtros de fecha si existen
      if (filters.fechaInicio) {
        params.fechaInicio = filters.fechaInicio;
      }
      if (filters.fechaFin) {
        params.fechaFin = filters.fechaFin;
      }
      
      const response = await fetchEntregasVertical(params);
      setRecolecciones(response.data || []);
      setTotalRecolecciones(response.total || 0);
    } catch (error) {
      console.error('Error al cargar recolecciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las recolecciones',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingRecolecciones(false);
    }
  };

  const loadHistorialDetallado = async () => {
    setIsLoadingHistorial(true);
    try {
      const params = {
        edificioId,
        limit: 1000, // Obtener todos los registros para la matriz
        // Excluir pendientes de confirmación y alistamientos (aún no impactan el stock)
        estado: { $nin: ['pendiente_confirmacion', 'alistado'] }
      };
      
      // Agregar filtros de fecha si existen
      if (filters.fechaInicio) {
        params.fechaInicio = filters.fechaInicio;
      }
      if (filters.fechaFin) {
        params.fechaFin = filters.fechaFin;
      }
      
      // Obtener todas las entregas (recolecciones + entregas)
      const response = await fetchEntregasVertical(params);
      const data = response.data || [];
      
      // Filtrar en el frontend también para asegurar que no se incluyan
      // pendientes de confirmación ni alistamientos
      const filteredData = data.filter(
        item => item.estado !== 'pendiente_confirmacion' && item.estado !== 'alistado'
      );
      
      // Ordenar por fecha ascendente (más antiguas primero para mostrar cronológicamente)
      const sortedData = filteredData.sort((a, b) => {
        const dateA = new Date(a.fecha_entrega || a.createdAt);
        const dateB = new Date(b.fecha_entrega || b.createdAt);
        return dateA - dateB;
      });
      
      setHistorialDetallado(sortedData);
      setTotalHistorial(sortedData.length);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el historial detallado',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingHistorial(false);
    }
  };

  // Construir matriz de prendas vs entregas/recolecciones
  const buildStockMatrix = () => {
    if (!historialDetallado.length) return { garments: [], events: [] };

    // Recopilar todas las prendas únicas
    const garmentMap = new Map();
    historialDetallado.forEach(event => {
      event.items?.forEach(item => {
        if (!garmentMap.has(item.id)) {
          garmentMap.set(item.id, {
            id: item.id,
            nombre: item.nombre,
            peso: item.peso
          });
        }
      });
    });

    const garments = Array.from(garmentMap.values()).sort((a, b) => 
      a.nombre.localeCompare(b.nombre)
    );

    // Preparar eventos con sus impactos
    const events = historialDetallado.map(event => {
      const isRecoleccion = event.estado === 'recoleccion';
      const itemsMap = new Map();
      
      event.items?.forEach(item => {
        itemsMap.set(item.id, {
          cantidad: item.cantidad,
          impacto: isRecoleccion ? item.cantidad : -item.cantidad
        });
      });

      return {
        id: event._id,
        consecutivo: event.consecutivo_recoleccion || event.solicitud_id || 'N/A',
        fecha: new Date(event.fecha_entrega || event.createdAt),
        tipo: isRecoleccion ? 'recoleccion' : 'entrega',
        conductor: event.conductor?.nombre || 'N/A',
        totalItems: event.total_items,
        itemsMap
      };
    });

    return { garments, events };
  };

  const toggleExpandItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleEntregaSuccess = () => {
    toast({
      title: 'Éxito',
      description: 'Entrega creada correctamente',
    });
    setShowCrearEntregaModal(false);
    // Recargar entregas si estamos en ese tab
    if (activeTab === 'entregas') {
      loadEntregas();
    }
  };

  const handleVerDetalle = (row) => {
    setSelectedEntrega(row);
    setShowDetalleModal(true);
  };

  const handleCloseDetalle = () => {
    setShowDetalleModal(false);
    setSelectedEntrega(null);
  };

  const handleVerAlistamiento = (row) => {
    setSelectedAlistamiento(row);
    setShowAlistamientoModal(true);
  };

  const handleCloseAlistamiento = () => {
    setShowAlistamientoModal(false);
    setSelectedAlistamiento(null);
  };

  const handleActionEntregas = (action, row) => {
    if (action === 'ver') {
      handleVerDetalle(row);
    } else if (action === 'verAlistamiento') {
      handleVerAlistamiento(row);
    } else if (action === 'editarAlistamiento') {
      setSelectedAlistamientoEdit(row);
      setShowEditarAlistamientoModal(true);
    }
  };

  const handleCloseEditarAlistamiento = () => {
    setShowEditarAlistamientoModal(false);
    setSelectedAlistamientoEdit(null);
  };

  const handleActionRecolecciones = async (action, row) => {
    if (action === 'ver') {
      setSelectedRecoleccion(row);
      setShowRecoleccionModal(true);
    } else if (action === 'eliminar') {
      await handleEliminarRecoleccion(row);
    }
  };

  const handleCloseRecoleccion = () => {
    setShowRecoleccionModal(false);
    setSelectedRecoleccion(null);
  };
  
  // Manejar aplicación de filtros
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setIsFilterDrawerOpen(false);
  };

  // Manejar eliminación de filtro individual
  const handleRemoveFilter = (filterId) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterId];
      return newFilters;
    });
  };
  
  // Contar filtros activos
  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key] !== undefined && filters[key] !== null && filters[key] !== '').length;
  };
  
  // Campos de filtro para el drawer
  const filterFields = [
    {
      id: 'fechaInicio',
      label: 'Fecha Inicio',
      type: 'date',
      placeholder: 'Fecha desde'
    },
    {
      id: 'fechaFin',
      label: 'Fecha Fin',
      type: 'date',
      placeholder: 'Fecha hasta'
    }
  ];

  // Columnas para Inventario
  const columnsInventario = [
    {
      id: 'tipo_prenda',
      label: 'Tipo de Prenda ',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Shirt className="h-4 w-4 text-purple-600" />
          <span className="font-medium">{row.tipo_prenda?.nombre || 'N/A'}</span>
        </div>
      ),
    },
    {
      id: 'cantidad_disponible',
      label: 'Disponible',
      render: (row) => (
        <Badge variant="outline" className="font-semibold">
          {row.cantidad_disponible || 0}
        </Badge>
      ),
    },
    {
      id: 'cantidad_minima',
      label: 'Mínimo',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.cantidad_minima || 0}</span>
      ),
    },
/*     {
      id: 'cantidad_en_lavado',
      label: 'En Lavado',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.cantidad_en_lavado || 0}</span>
      ),
    }, */
    {
      id: 'estado',
      label: 'Estado',
      render: (row) => {
        const estadoColors = {
          disponible: 'bg-green-100 text-green-800',
          bajo_stock: 'bg-yellow-100 text-yellow-800',
          agotado: 'bg-red-100 text-red-800',
          inactivo: 'bg-gray-100 text-gray-800',
        };
        return (
          <Badge className={estadoColors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado || 'N/A'}
          </Badge>
        );
      },
    },

  ];

  // Columnas para Movimientos
  const columnsMovimientos = [
    {
      id: 'fecha',
      label: 'Fecha',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.fecha_movimiento || row.createdAt).toLocaleDateString('es-ES')}
          </span>
        </div>
      ),
    },
    {
      id: 'tipo_movimiento',
      label: 'Tipo',
      render: (row) => {
        const tipoColors = {
          entrada: 'bg-green-100 text-green-800',
          salida: 'bg-red-100 text-red-800',
          traslado: 'bg-blue-100 text-blue-800',
          ajuste: 'bg-yellow-100 text-yellow-800',
          baja: 'bg-gray-100 text-gray-800',
        };
        return (
          <Badge className={tipoColors[row.tipo_movimiento] || 'bg-gray-100 text-gray-800'}>
            {row.tipo_movimiento || 'N/A'}
          </Badge>
        );
      },
    },
    {
      id: 'tipo_prenda',
      label: 'Prenda',
      render: (row) => (
        <span className="text-sm">{row.tipo_prenda?.nombre || 'N/A'}</span>
      ),
    },
    {
      id: 'cantidad',
      label: 'Cantidad',
      render: (row) => (
        <span className="font-semibold">{row.cantidad || 0}</span>
      ),
    },
    {
      id: 'usuario',
      label: 'Usuario',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.usuario?.nombre || 'N/A'}</span>
      ),
    },
    {
      id: 'consecutivo',
      label: 'Consecutivo',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.consecutivo || 'N/A'}</span>
      ),
    },
  ];

  // Columnas para Entregas
  const columnsEntregas = [
    {
      id: 'fecha',
      label: 'Fecha',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString('es-ES')}
          </span>
        </div>
      ),
    },
    {
      id: 'total_items',
      label: 'Items',
      render: (row) => (
        <Badge variant="outline">{row.total_items || 0}</Badge>
      ),
    },
    {
      id: 'peso_total',
      label: 'Peso Estimado',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.peso_total ? `${(row.peso_total / 1000).toFixed(2)} kg` : '-'}
        </span>
      ),
    },
    {
      id: 'peso_real',
      label: 'Peso Real',
      render: (row) => (
        <span className="text-sm font-medium text-gray-800">
          {row.peso_real ? `${(row.peso_real / 1000).toFixed(2)} kg` : '-'}
        </span>
      ),
    },
    {
      id: 'conductor',
      label: 'Conductor',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.conductor?.nombre || 'N/A'}</span>
      ),
    },
    {
      id: 'estado',
      label: 'Estado',
      render: (row) => {
        const estadoColors = {
          pendiente: 'bg-yellow-100 text-yellow-800',
          alistado: 'bg-blue-100 text-blue-800',
          en_camino: 'bg-purple-100 text-purple-800',
          entregado: 'bg-green-100 text-green-800',
          cancelado: 'bg-red-100 text-red-800',
          recoleccion: 'bg-orange-100 text-orange-800',
        };
        return (
          <Badge className={estadoColors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado || 'N/A'}
          </Badge>
        );
      },
    },
    {
      id: 'solicitud_id',
      label: 'Consecutivo',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.solicitud_id || 'N/A'}</span>
      ),
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver',
          icon: 'FileText',
          tooltip: 'Ver Comprobante',
          className: 'text-green-600 hover:bg-green-50',
          // El comprobante de entrega solo aplica cuando ya fue entregado
          showWhen: (row) => row.estado === 'entregado',
        },
        {
          key: 'verAlistamiento',
          icon: 'Package',
          tooltip: 'Ver Alistamiento',
          className: 'text-blue-600 hover:bg-blue-50',
          // Mientras no esté entregado se muestra el ticket de alistamiento
          showWhen: (row) => row.estado !== 'entregado',
        },
        {
          key: 'editarAlistamiento',
          icon: 'Edit',
          tooltip: 'Editar Alistamiento',
          className: 'text-amber-600 hover:bg-amber-50',
          // Solo se puede editar antes de ponerlo en ruta
          showWhen: (row) => row.estado === 'alistado',
        },
      ],
    },
  ];

  // Columnas para Recolecciones
  const columnsRecolecciones = [
    {
      id: 'fecha',
      label: 'Fecha',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.fecha_entrega || row.createdAt).toLocaleDateString('es-ES')}
          </span>
        </div>
      ),
    },

    {
      id: 'total_items',
      label: 'Items',
      render: (row) => (
        <Badge variant="outline">{row.total_items || 0}</Badge>
      ),
    },
    {
      id: 'peso_total',
      label: 'Peso Estimado',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.peso_total ? `${(row.peso_total / 1000).toFixed(2)} kg` : '-'}
        </span>
      ),
    },
    {
      id: 'peso_real',
      label: 'Peso Real',
      render: (row) => (
        <span className="text-sm font-medium text-gray-800">
          {row.peso_real ? `${(row.peso_real / 1000).toFixed(2)} kg` : '-'}
        </span>
      ),
    },
    {
      id: 'conductor',
      label: 'Conductor',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.conductor?.nombre || 'N/A'}</span>
      ),
    },
    {
      id: 'estado',
      label: 'Estado',
      render: (row) => (
        <Badge className="bg-orange-100 text-orange-800">
          <TruckIcon className="h-3 w-3 mr-1" />
          Recolección
        </Badge>
      ),
    },
        {
      id: 'consecutivo',
      label: 'Consecutivo',
      render: (row) => (
        <span className="font-mono text-sm font-medium">{row?.consecutivo_recoleccion || row?.solicitud_id || 'N/A'}</span>
      ),
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver',
          icon: 'FileText',
          tooltip: 'Ver Comprobante',
          className: 'text-orange-600 hover:bg-orange-50',
        },
        {
          key: 'eliminar',
          icon: 'Trash2',
          tooltip: 'Eliminar Recolección',
          className: 'text-red-600 hover:bg-red-50',
        },
      ],
    },
  ];

  const handleAction = async (actionKey, row) => {
    if (actionKey === 'eliminar') {
      await handleEliminarRecoleccion(row);
    } else if (actionKey === 'ver') {
      toast({
        title: 'Acción',
        description: `Ver comprobante - ${row._id}`,
      });
    }
  };

  const handleEliminarRecoleccion = async (recoleccion) => {
    // Confirmar eliminación
    if (!window.confirm(`¿Está seguro de eliminar la recolección ${recoleccion.consecutivo_recoleccion || recoleccion.solicitud_id}?\n\nEsto eliminará:\n- La recolección\n- Los movimientos asociados\n- Actualizará el inventario`)) {
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${API_URL}/entregas-lenceria-vertical/eliminar-recoleccion/${recoleccion._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar recolección');
      }

      const result = await response.json();

      toast({
        title: 'Éxito',
        description: result.message || 'Recolección eliminada correctamente',
      });

      // Recargar datos
      loadRecolecciones();
      loadInventario();
      loadMovimientos();
    } catch (error) {
      console.error('Error al eliminar recolección:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar la recolección',
        variant: 'destructive',
      });
    }
  };

  if (!edificio) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Cargando información del edificio...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    if (fromHospedajes) {
                      navigate(-1); // Volver a la página anterior (HospedajesTable)
                    } else {
                      navigate('/lavanderia/inventario-vertical');
                    }
                  }}
                  className="h-10 w-10"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Building2 className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {edificio.nombre}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {edificio.ciudad} • {edificio.direccion}
                  </CardDescription>
                </div>
              </div>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                Vertical
              </Badge>
            </div>
          </CardHeader>
        </Card>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-6 bg-gray-100 p-1 rounded-lg h-auto gap-1">
                <TabsTrigger 
                  value="inventario" 
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-purple-700 data-[state=active]:font-semibold data-[state=active]:border-purple-200 hover:bg-gray-50 hover:border-gray-300"
                >
                  <Package className="h-4 w-4" />
                  <span className="hidden sm:inline">Inventario en lavandería</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="movimientos" 
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-700 data-[state=active]:font-semibold data-[state=active]:border-blue-200 hover:bg-gray-50 hover:border-gray-300"
                >
                  <TrendingUp className="h-4 w-4" />
                  <span className="hidden sm:inline">Movimientos</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="recolecciones" 
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-orange-700 data-[state=active]:font-semibold data-[state=active]:border-orange-200 hover:bg-gray-50 hover:border-gray-300"
                >
                  <TruckIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Recolecciones</span>
              
                </TabsTrigger>
                <TabsTrigger 
                  value="entregas" 
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-green-700 data-[state=active]:font-semibold data-[state=active]:border-green-200 hover:bg-gray-50 hover:border-gray-300"
                >
                  <TruckIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Entregas y Alistamientos</span>
            
                </TabsTrigger>
                <TabsTrigger 
                  value="historial" 
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-indigo-700 data-[state=active]:font-semibold data-[state=active]:border-indigo-200 hover:bg-gray-50 hover:border-gray-300"
                >
                  <History className="h-4 w-4" />
                  <span className="hidden sm:inline">Historial Detallado</span>
    
                </TabsTrigger>
              </TabsList>

              {/* Tab Inventario */}
              <TabsContent value="inventario" className="space-y-4">
                {/* Total de prendas */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Package className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Prendas pendientes por entregar</p>
                        <p className="text-2xl font-bold text-purple-700">{totalPrendas}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-end gap-4">
                  <ExportExcelButton
                    service={async () => {
                      const response = await fetchInventarioVertical({
                        edificioId,
                        limit: 10000
                      });
                      return response.data || [];
                    }}
                    filters={{}}
                    columns={[
                      { header: 'Tipo de Prenda', key: 'tipo_prenda.nombre' },
                      { header: 'Disponible', key: 'cantidad_disponible' },
                      { header: 'Mínimo', key: 'cantidad_minima' },
                      { header: 'Estado', key: 'estado' },
                    ]}
                    fileName={`inventario-${edificio?.nombre || 'edificio'}-${new Date().toISOString().split('T')[0]}`}
                  />
                </div>

                <DataTable
                  columns={columnsInventario}
                  data={inventario}
                  isLoading={isLoadingInventario}
                  onAction={handleAction}
                  showDefaultActions={false}
                />
                <Pagination
                  currentPage={currentPageInventario}
                  totalItems={totalInventario}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPageInventario}
                />
              </TabsContent>

              {/* Tab Movimientos */}
              <TabsContent value="movimientos" className="space-y-4">
                {/* Filtros aplicados */}
                {getActiveFiltersCount() > 0 && (
                  <AppliedFilters
                    filters={filters}
                    onRemoveFilter={handleRemoveFilter}
                    filterFields={filterFields}
                  />
                )}
                
                <div className="flex items-center justify-end gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filtros
                    {getActiveFiltersCount() > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {getActiveFiltersCount()}
                      </Badge>
                    )}
                  </Button>
                  <ExportExcelButton
                    service={async () => {
                      const params = {
                        edificioId,
                        limit: 10000
                      };
                      if (filters.fechaInicio) params.fechaInicio = filters.fechaInicio;
                      if (filters.fechaFin) params.fechaFin = filters.fechaFin;
                      const response = await fetchMovimientosVertical(params);
                      return response.data || [];
                    }}
                    filters={{}}
                    columns={[
                      { header: 'Fecha', key: 'fecha_movimiento' },
                      { header: 'Tipo', key: 'tipo_movimiento' },
                      { header: 'Prenda', key: 'tipo_prenda.nombre' },
                      { header: 'Cantidad', key: 'cantidad' },
                      { header: 'Motivo', key: 'motivo' },
                      { header: 'Consecutivo', key: 'consecutivo' },
                    ]}
                    fileName={`movimientos-${edificio?.nombre || 'edificio'}-${new Date().toISOString().split('T')[0]}`}
                  />
                </div>

                <DataTable
                  columns={columnsMovimientos}
                  data={movimientos}
                  isLoading={isLoadingMovimientos}
                  onAction={handleAction}
                  showDefaultActions={false}
                />
                <Pagination
                  currentPage={currentPageMovimientos}
                  totalItems={totalMovimientos}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPageMovimientos}
                />
              </TabsContent>

              {/* Tab Recolecciones */}
              <TabsContent value="recolecciones" className="space-y-4">
                {/* Filtros aplicados */}
                {getActiveFiltersCount() > 0 && (
                  <AppliedFilters
                    filters={filters}
                    onRemoveFilter={handleRemoveFilter}
                    filterFields={filterFields}
                  />
                )}
                
                <div className="flex items-center justify-end gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filtros
                    {getActiveFiltersCount() > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {getActiveFiltersCount()}
                      </Badge>
                    )}
                  </Button>
                  <ExportExcelButton
                    service={async () => {
                      const params = {
                        edificioId,
                        estado: 'recoleccion',
                        limit: 10000
                      };
                      if (filters.fechaInicio) params.fechaInicio = filters.fechaInicio;
                      if (filters.fechaFin) params.fechaFin = filters.fechaFin;
                      const response = await fetchEntregasVertical(params);
                      return response.data || [];
                    }}
                    filters={{}}
                    columns={[
                      { header: 'Consecutivo', key: 'consecutivo_recoleccion' },
                      { header: 'Fecha', key: 'fecha_entrega' },
                      { header: 'Conductor', key: 'conductor.nombre' },
                      { header: 'Total Items', key: 'total_items' },
                      { header: 'Peso Estimado (kg)', key: 'peso_total' },
                      { header: 'Peso Real (kg)', key: 'peso_real' },
                    ]}
                    fileName={`recolecciones-${edificio?.nombre || 'edificio'}-${new Date().toISOString().split('T')[0]}`}
                    dataMapper={(item) => ({
                      ...item,
                      peso_total: item.peso_total ? (item.peso_total / 1000).toFixed(2) : '0',
                      peso_real: item.peso_real ? (item.peso_real / 1000).toFixed(2) : '',
                    })}
                  />
                </div>

                <DataTable
                  columns={columnsRecolecciones}
                  data={recolecciones}
                  isLoading={isLoadingRecolecciones}
                  onAction={handleActionRecolecciones}
                />
                <Pagination
                  currentPage={currentPageRecolecciones}
                  totalItems={totalRecolecciones}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPageRecolecciones}
                />
              </TabsContent>

              {/* Tab Entregas */}
              <TabsContent value="entregas" className="space-y-4">
                {/* Filtros aplicados */}
                {getActiveFiltersCount() > 0 && (
                  <AppliedFilters
                    filters={filters}
                    onRemoveFilter={handleRemoveFilter}
                    filterFields={filterFields}
                  />
                )}
                
                <div className="flex items-center justify-end gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filtros
                    {getActiveFiltersCount() > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {getActiveFiltersCount()}
                      </Badge>
                    )}
                  </Button>
                  <ExportExcelButton
                    service={async () => {
                      const params = {
                        edificioId,
                        estado: { $nin: ['recoleccion', 'pendiente_confirmacion'] },
                        limit: 10000
                      };
                      if (filters.fechaInicio) params.fechaInicio = filters.fechaInicio;
                      if (filters.fechaFin) params.fechaFin = filters.fechaFin;
                      const response = await fetchEntregasVertical(params);
                      return response.data || [];
                    }}
                    filters={{}}
                    columns={[
                      { header: 'Consecutivo', key: 'consecutivo_entrega' },
                      { header: 'Fecha', key: 'fecha_entrega' },
                      { header: 'Conductor', key: 'conductor.nombre' },
                      { header: 'Receptor', key: 'receptor_nombre' },
                      { header: 'Total Items', key: 'total_items' },
                      { header: 'Peso Estimado (kg)', key: 'peso_total' },
                      { header: 'Peso Real (kg)', key: 'peso_real' },
                      { header: 'Estado', key: 'estado' },
                    ]}
                    fileName={`entregas-${edificio?.nombre || 'edificio'}-${new Date().toISOString().split('T')[0]}`}
                    dataMapper={(item) => ({
                      ...item,
                      peso_total: item.peso_total ? (item.peso_total / 1000).toFixed(2) : '0',
                      peso_real: item.peso_real ? (item.peso_real / 1000).toFixed(2) : '',
                    })}
                  />
                  {!fromHospedajes && (
                    <Button 
                      className="bg-purple-600 hover:bg-purple-700"
                      onClick={() => setShowCrearEntregaModal(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Alistamiento
                    </Button>
                  )}
                </div>

                <DataTable
                  columns={columnsEntregas}
                  data={entregas}
                  isLoading={isLoadingEntregas}
                  onAction={handleActionEntregas}
                />
                <Pagination
                  currentPage={currentPageEntregas}
                  totalItems={totalEntregas}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPageEntregas}
                />
              </TabsContent>

              {/* Tab Historial Detallado - Matriz */}
              <TabsContent value="historial" className="space-y-4">
                {/* Filtros aplicados */}
                {getActiveFiltersCount() > 0 && (
                  <AppliedFilters
                    filters={filters}
                    onRemoveFilter={handleRemoveFilter}
                    filterFields={filterFields}
                  />
                )}
                
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    Matriz de impacto: Prendas (filas) × Recolecciones/Entregas (columnas)
                  </div>
                  <div className="flex items-center gap-2">
                    <ExportExcelButton
                      service={async () => {
                        const { garments, events } = buildStockMatrix();
                        return garments.map(garment => {
                          const row = { prenda: garment.nombre, peso: `${garment.peso}g` };
                          let totalRecibido = 0;
                          let totalEntregado = 0;
                          
                          events.forEach((event, idx) => {
                            const itemData = event.itemsMap.get(garment.id);
                            const impacto = itemData?.impacto || 0;
                            row[`${event.tipo === 'recoleccion' ? 'REC' : 'ENT'}_${event.consecutivo.substring(0, 10)}`] = impacto;
                            
                            if (impacto > 0) totalRecibido += impacto;
                            else if (impacto < 0) totalEntregado += Math.abs(impacto);
                          });
                          
                          row['Total Recibido'] = totalRecibido;
                          row['Total Entregado'] = totalEntregado;
                          row['Total Actual'] = totalRecibido - totalEntregado;
                          return row;
                        });
                      }}
                      filters={{}}
                      columns={[]}
                      fileName={`historial-detallado-${edificio?.nombre || 'edificio'}-${new Date().toISOString().split('T')[0]}`}
                    />
                    <Button
                      variant="outline"
                      onClick={() => setIsFilterDrawerOpen(true)}
                      className="flex items-center gap-2"
                    >
                      <Filter className="h-4 w-4" />
                      Filtros
                      {getActiveFiltersCount() > 0 && (
                        <Badge variant="secondary" className="ml-1">
                          {getActiveFiltersCount()}
                        </Badge>
                      )}
                    </Button>
                  </div>
                </div>

                {isLoadingHistorial ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                      <p className="text-gray-500">Cargando historial...</p>
                    </div>
                  </div>
                ) : historialDetallado.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No hay registros en el historial</p>
                    </CardContent>
                  </Card>
                ) : (() => {
                  const { garments, events } = buildStockMatrix();
                  
                  return (
                    <Card>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead className="sticky top-0 bg-gray-50 z-10">
                              <tr>
                                <th className="sticky left-0 bg-gray-100 border border-gray-300 p-3 text-left font-semibold text-sm min-w-[200px] z-20">
                                  <div className="flex items-center gap-2">
                                    <Shirt className="h-4 w-4 text-purple-600" />
                                    Prenda
                                  </div>
                                </th>
                                {events.map((event, idx) => (
                                  <th 
                                    key={event.id}
                                    className={`border border-gray-300 p-2 text-center font-semibold text-xs min-w-[120px] ${
                                      event.tipo === 'recoleccion' ? 'bg-orange-50' : 'bg-green-50'
                                    }`}
                                  >
                                    <div className="flex flex-col items-center gap-1">
                                      <div className="flex items-center gap-1">
                                        {event.tipo === 'recoleccion' ? (
                                          <ArrowUp className="h-3 w-3 text-orange-600" />
                                        ) : (
                                          <ArrowDown className="h-3 w-3 text-green-600" />
                                        )}
                                        <span className={event.tipo === 'recoleccion' ? 'text-orange-700' : 'text-green-700'}>
                                          {event.tipo === 'recoleccion' ? 'REC' : 'ENT'}
                                        </span>
                                      </div>
                                      <div className="text-xs font-mono text-gray-600">
                                        {event.consecutivo.substring(0, 15)}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {event.fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                                      </div>
                                    </div>
                                  </th>
                                ))}
                                <th className="bg-green-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px]">
                                  <div className="text-green-700">Total<br/>Recibido</div>
                                </th>
                                <th className="bg-orange-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px]">
                                  <div className="text-orange-700">Total<br/>Entregado</div>
                                </th>
                                <th className="sticky right-0 bg-blue-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px] z-20">
                                  <div className="text-blue-700">Total<br/>Actual</div>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {garments.map((garment, gIdx) => {
                                let totalRecibido = 0;
                                let totalEntregado = 0;
                                
                                return (
                                  <tr key={garment.id} className={gIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="sticky left-0 border border-gray-300 p-3 font-medium text-sm z-10 bg-inherit">
                                      <div className="flex items-center gap-2">
                                        <Shirt className="h-3 w-3 text-purple-600 flex-shrink-0" />
                                        <span className="truncate">{garment.nombre}</span>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {garment.peso}g
                                      </div>
                                    </td>
                                    {events.map((event) => {
                                      const itemData = event.itemsMap.get(garment.id);
                                      const impacto = itemData?.impacto || 0;
                                      
                                      if (impacto > 0) totalRecibido += impacto;
                                      else if (impacto < 0) totalEntregado += Math.abs(impacto);
                                      
                                      return (
                                        <td 
                                          key={event.id}
                                          className="border border-gray-300 p-2 text-center"
                                        >
                                          {impacto !== 0 ? (
                                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                              impacto > 0 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-orange-100 text-orange-700'
                                            }`}>
                                              {impacto > 0 ? '+' : ''}{impacto}
                                            </div>
                                          ) : (
                                            <span className="text-gray-300 text-xs">—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td className="bg-green-50 border border-gray-300 p-2 text-center font-bold text-sm">
                                      <div className="text-green-700">
                                        +{totalRecibido}
                                      </div>
                                    </td>
                                    <td className="bg-orange-50 border border-gray-300 p-2 text-center font-bold text-sm">
                                      <div className="text-orange-700">
                                        -{totalEntregado}
                                      </div>
                                    </td>
                                    <td className="sticky right-0 bg-blue-50 border border-gray-300 p-2 text-center font-bold text-sm z-10">
                                      <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full ${
                                        (totalRecibido - totalEntregado) < 0 
                                          ? 'bg-red-100 text-red-700' 
                                          : (totalRecibido - totalEntregado) > 0
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-gray-100 text-gray-700'
                                      }`}>
                                        {(totalRecibido - totalEntregado) > 0 ? '+' : ''}{totalRecibido - totalEntregado}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="sticky bottom-0 bg-gray-100">
                              <tr>
                                <td className="sticky left-0 border border-gray-300 p-3 font-bold text-sm bg-gray-100 z-20">
                                  Total por Evento
                                </td>
                                {events.map((event) => {
                                  const total = Array.from(event.itemsMap.values()).reduce(
                                    (sum, item) => sum + item.impacto, 
                                    0
                                  );
                                  
                                  return (
                                    <td 
                                      key={event.id}
                                      className={`border border-gray-300 p-2 text-center font-bold text-sm ${
                                        event.tipo === 'recoleccion' ? 'bg-orange-50' : 'bg-green-50'
                                      }`}
                                    >
                                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                                        total < 0 
                                          ? 'bg-orange-200 text-orange-800' 
                                          : 'bg-green-200 text-green-800'
                                      }`}>
                                        {total > 0 ? '+' : ''}{total}
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="bg-green-100 border border-gray-300 p-2 text-center font-bold text-sm">
                                  <div className="text-green-800">
                                    {garments.reduce((sum, g) => {
                                      let total = 0;
                                      events.forEach(e => {
                                        const imp = e.itemsMap.get(g.id)?.impacto || 0;
                                        if (imp > 0) total += imp;
                                      });
                                      return sum + total;
                                    }, 0)}
                                  </div>
                                </td>
                                <td className="bg-orange-100 border border-gray-300 p-2 text-center font-bold text-sm">
                                  <div className="text-orange-800">
                                    {garments.reduce((sum, g) => {
                                      let total = 0;
                                      events.forEach(e => {
                                        const imp = e.itemsMap.get(g.id)?.impacto || 0;
                                        if (imp < 0) total += Math.abs(imp);
                                      });
                                      return sum + total;
                                    }, 0)}
                                  </div>
                                </td>
                                <td className="sticky right-0 bg-blue-100 border border-gray-300 p-2 text-center font-bold text-sm z-20">
                                  <div className="text-blue-800">
                                    {garments.reduce((sum, g) => {
                                      let rec = 0, ent = 0;
                                      events.forEach(e => {
                                        const imp = e.itemsMap.get(g.id)?.impacto || 0;
                                        if (imp > 0) rec += imp;
                                        else if (imp < 0) ent += Math.abs(imp);
                                      });
                                      return sum + (rec - ent);
                                    }, 0)}
                                  </div>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        
                        {/* Leyenda */}
                        <div className="p-4 bg-gray-50 border-t border-gray-300">
                          <div className="flex items-center justify-center gap-6 text-xs">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-green-100 border border-green-300"></div>
                              <span className="text-gray-600">Recolección (+) suma al inventario</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-orange-100 border border-orange-300"></div>
                              <span className="text-gray-600">Entrega (-) resta del inventario</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-700">Total:</span>
                              <span className="text-gray-600">Impacto acumulado por prenda</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>

      {/* Modal Crear Entrega */}
      <CrearEntregaDirectaModal
        isOpen={showCrearEntregaModal}
        onClose={() => setShowCrearEntregaModal(false)}
        onSuccess={handleEntregaSuccess}
        edificio={edificio}
      />

      {/* Modal Detalle Entrega (comprobante - solo entregados) */}
      <DetalleEntregaVerticalModal
        isOpen={showDetalleModal}
        onClose={handleCloseDetalle}
        entrega={selectedEntrega}
      />

      {/* Modal exclusivo de Alistamiento (no entregados) */}
      <DetalleAlistamientoModal
        isOpen={showAlistamientoModal}
        onClose={handleCloseAlistamiento}
        alistamiento={selectedAlistamiento}
      />

      {/* Modal de Recolección (ropa sucia) */}
      <DetalleRecoleccionModal
        isOpen={showRecoleccionModal}
        onClose={handleCloseRecoleccion}
        recoleccion={selectedRecoleccion}
      />

      {/* Modal Editar Alistamiento (antes de ponerlo en ruta) */}
      <EditarAlistamientoModal
        isOpen={showEditarAlistamientoModal}
        onClose={handleCloseEditarAlistamiento}
        alistamiento={selectedAlistamientoEdit}
        onSuccess={() => {
          loadEntregas();
          handleCloseEditarAlistamiento();
        }}
      />
      
      {/* FilterDrawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApplyFilters={handleApplyFilters}
        fields={filterFields}
        initialFilters={filters}
      />
    </div>
  );
};

export default EdificioInventarioDetail;
